// Field equipment bundle widget — PDP add-on bundle.
// Auto-initializes every [data-feq-bundle-block] on the page.
// Per-PDP data (machine variant map, sub-twin variants, locale) is read from
// a <script type="application/json" data-feq-config> blob inside each block.
(function () {
  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }
  ready(function () {
    document.querySelectorAll('[data-feq-bundle-block]').forEach(initBlock);
  });

  function initBlock(block) {
    const cfgEl = block.querySelector('[data-feq-config]');
    if (!cfgEl) { console.warn('[feq-bundle] no config blob in block', block); return; }
    let cfg;
    try { cfg = JSON.parse(cfgEl.textContent); }
    catch (e) { console.error('[feq-bundle] config parse error', e); return; }

    const isDe = !!cfg.isDe;
    const currency = cfg.currency || 'EUR';
    const machineVariantMap = cfg.machineVariantMap || {};
    const subTwinVariants = cfg.subTwinVariants || {};
    const coffeeOfferVariants = cfg.coffeeOfferVariants || {};
    const coffeeOffCents = parseInt(cfg.coffeeOffCents || 0, 10);
    // Assigned when the coffee-offer row exists; a no-op on every other machine.
    let refreshCoffeeOfferMachine = () => {};
    const coffeeOfferVariantIds = new Set();
    Object.values(coffeeOfferVariants).forEach(list => (list || []).forEach(v => coffeeOfferVariantIds.add(String(v.id))));
    const subTwinImages = cfg.subTwinImages || {};

    const productForm = block.closest('product-form') || document.querySelector('product-form');
    const form = (productForm && productForm.querySelector('form')) || document.querySelector('form[action*="/cart/add"]');
    if (!form) { console.warn('[feq-bundle] no product form found'); return; }
    const submitBtn = form.querySelector('[type="submit"]');
    if (!submitBtn) { console.warn('[feq-bundle] no submit button found'); return; }
    console.log('[feq-bundle] initialized', { block, form });

    const moneyFormatter = new Intl.NumberFormat(isDe ? 'de-DE' : 'en-IE', { style: 'currency', currency });
    function formatMoney(cents) { return moneyFormatter.format(cents / 100); }
    const moneyFormatterShort = new Intl.NumberFormat(isDe ? 'de-DE' : 'en-IE', { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0 });
    function formatMoneyShort(cents) { return cents % 100 === 0 ? moneyFormatterShort.format(cents / 100) : formatMoney(cents); }
    function getMachineVariantId() { return form.querySelector('input[name="id"]')?.value; }
    function getMachineColour() {
      const id = getMachineVariantId();
      return id && machineVariantMap[id] ? machineVariantMap[id].colour : '';
    }

    // Update the displayed price on a row to match its currently selected variant.
    function updateRowPrice(item) {
      const select = item.querySelector('[data-feq-select]');
      if (!select) return;
      const opt = select.options[select.selectedIndex];
      if (!opt) return;
      const fullPrice = parseInt(opt.dataset.price || '0', 10);
      const discountPct = parseInt(item.dataset.discountPercent || '0', 10);
      const quantity = Math.max(1, parseInt(item.dataset.dealQuantity || '1', 10));
      const priceNow = item.querySelector('.feq-bundle__price-now');
      const priceWas = item.querySelector('.feq-bundle__price-was');
      const qtyPrefix = quantity > 1 ? `${quantity}× ` : '';
      if (priceNow) {
        if (discountPct === 100) {
          priceNow.textContent = isDe ? 'GRATIS' : 'FREE';
          priceNow.classList.add('feq-bundle__price-free');
        } else if (discountPct > 0) {
          priceNow.textContent = qtyPrefix + formatMoney(Math.round(fullPrice * (100 - discountPct) / 100));
          priceNow.classList.remove('feq-bundle__price-free');
        } else {
          priceNow.textContent = qtyPrefix + formatMoney(fullPrice);
          priceNow.classList.remove('feq-bundle__price-free');
        }
      }
      if (priceWas) {
        if (discountPct > 0) {
          priceWas.textContent = formatMoney(fullPrice * quantity);
          priceWas.style.display = '';
        } else {
          const cmp = parseInt(opt.dataset.comparePrice || '0', 10);
          if (cmp > fullPrice) {
            priceWas.textContent = formatMoney(cmp);
            priceWas.style.display = '';
          } else {
            priceWas.style.display = 'none';
          }
        }
      }

      // Swap the row image to match the selected variant (e.g. Knock Box colour,
      // Encore ESP White/Black). Falls back to the product image via data-image.
      const rowImg = item.querySelector('[data-feq-row-image]');
      if (rowImg && opt.dataset.image && rowImg.getAttribute('src') !== opt.dataset.image) {
        rowImg.src = opt.dataset.image;
        rowImg.removeAttribute('srcset'); // stop the old srcset overriding the new src
      }
    }

    const manuallyChanged = new WeakSet();
    block.querySelectorAll('[data-feq-select]').forEach(sel => {
      sel.addEventListener('input', () => manuallyChanged.add(sel));
      sel.addEventListener('change', () => {
        manuallyChanged.add(sel);
        const item = sel.closest('[data-feq-item]');
        if (item) updateRowPrice(item);
      });
    });

    // ── Sub-gift wiring ─────────────────────────────────────────────
    const subRow = block.querySelector('[data-feq-sub-gift]');
    if (subRow) {
      const coffeeSel = subRow.querySelector('[data-feq-sub-coffee]');
      const grindSel = subRow.querySelector('[data-feq-sub-grind]');
      const freqSel = subRow.querySelector('[data-feq-sub-freq]');
      const subImageEl = subRow.querySelector('[data-feq-sub-image]');
      const subValueEl = subRow.querySelector('[data-feq-sub-value]');
      const subWasEl = subRow.querySelector('[data-feq-sub-was]');
      const subFinePrintEl = subRow.querySelector('[data-feq-sub-fineprint]');
      const subTagPriceEl = subRow.querySelector('[data-feq-sub-tag-price]');
      const subTagCadenceEl = subRow.querySelector('[data-feq-sub-tag-cadence]');
      const subN = Math.max(1, parseInt(subRow.dataset.subN || '1', 10));
      const subNPlus1 = subN + 1;
      // Post-intro subscriber discount, read from the resolved selling plan via data-sub-post-pct
      // (Liquid takes the LAST price_adjustment on the plan: 5% on the flat "Field Sage Subscription"
      // plan, 0% on the 12-delivery plan whose afterCycle step drops to full price). Falls back to 5
      // only if the attribute is absent, preserving the previous behaviour for older markup.
      const subPostPctRaw = parseFloat(subRow.dataset.subPostPct);
      const POST_INTRO_DISCOUNT = (Number.isFinite(subPostPctRaw) ? subPostPctRaw : 5) / 100;

      // Parse a frequency-option's display text (e.g. "15 Days", "30 Days",
      // "1 Week") into { count, unit } for default-selection and fine-print
      // formatting.
      // Locale-aware. The selling-plan names in this select are TRANSLATED, so an English-only
      // match silently fails on /de: the 30-day default below never fires and first-time
      // subscribers land on the most aggressive 15-day cadence instead.
      var FREQ_UNITS = [
        [/(\d+)\s*(?:tage|tag|days|day)\b/, "day"],
        [/(\d+)\s*(?:wochen|woche|weeks|week)\b/, "week"],
        [/(\d+)\s*(?:monate|monat|months|month)\b/, "month"],
        [/(\d+)\s*(?:jahre|jahr|years|year)\b/, "year"]
      ];
      var FREQ_BARE = [
        [/täglich|taeglich|daily/, "day"],
        [/wöchentlich|woechentlich|weekly/, "week"],
        [/monatlich|monthly/, "month"],
        [/jährlich|jaehrlich|yearly|annual/, "year"]
      ];
      function parseFreqOption(opt) {
        if (!opt) return null;
        var t = (opt.textContent || "").toLowerCase();
        for (var a = 0; a < FREQ_UNITS.length; a++) {
          var m = t.match(FREQ_UNITS[a][0]);
          if (m) return { count: parseInt(m[1], 10), unit: FREQ_UNITS[a][1] };
        }
        // No number, e.g. "Monatlich, erste 12 Lieferungen gratis" - treat as every 1 unit.
        for (var b = 0; b < FREQ_BARE.length; b++) {
          if (FREQ_BARE[b][0].test(t)) return { count: 1, unit: FREQ_BARE[b][1] };
        }
        return null;
      }

      // Default frequency to 30-day cadence on first render (less aggressive
      // than 15 days; reduces pre-charge cancellation pressure on first-time
      // subscribers who just bought an espresso machine).
      if (freqSel && freqSel.options.length) {
        let pickIdx = -1;
        for (let i = 0; i < freqSel.options.length; i++) {
          const p = parseFreqOption(freqSel.options[i]);
          if (p && p.count === 30 && p.unit === 'day') { pickIdx = i; break; }
        }
        if (pickIdx >= 0) freqSel.selectedIndex = pickIdx;
      }

      // Format the cadence for the fine print ("every 30 days" / "alle 30 Tage").
      function formatCadence() {
        const p = parseFreqOption(freqSel?.options[freqSel.selectedIndex]);
        if (!p) return '';
        const unitMap = isDe
          ? { day: 'Tage', week: 'Wochen', month: 'Monate', year: 'Jahre' }
          : { day: 'days', week: 'weeks', month: 'months', year: 'years' };
        // Singular reads badly as "alle 1 Monate" / "every 1 months".
        if (p.count === 1) {
          const singularMap = isDe
            ? { day: "pro Tag", week: "pro Woche", month: "pro Monat", year: "pro Jahr" }
            : { day: "per day", week: "per week", month: "per month", year: "per year" };
          return singularMap[p.unit] || (isDe ? "pro Lieferung" : "per delivery");
        }
        const unitLabel = unitMap[p.unit] || p.unit;
        return isDe ? `alle ${p.count} ${unitLabel}` : `every ${p.count} ${unitLabel}`;
      }

      function rebuildGrindOptions() {
        if (!coffeeSel || !grindSel) return;
        const twinId = coffeeSel.value;
        const variants = subTwinVariants[twinId] || [];
        const prevGrindLabel = grindSel.options[grindSel.selectedIndex]?.textContent;
        grindSel.innerHTML = '';
        let preferredIndex = 0;
        variants.forEach((v, i) => {
          const opt = document.createElement('option');
          opt.value = String(v.id);
          opt.dataset.price = String(v.price);
          opt.textContent = v.grind;
          grindSel.appendChild(opt);
          if (prevGrindLabel && v.grind === prevGrindLabel) preferredIndex = i;
          else if (!prevGrindLabel && /whole bean/i.test(v.grind)) preferredIndex = i;
        });
        if (variants.length) grindSel.selectedIndex = preferredIndex;
      }

      function updateSubRowDisplay() {
        const coffeeOpt = coffeeSel?.options[coffeeSel.selectedIndex];
        const grindOpt = grindSel?.options[grindSel.selectedIndex];
        if (!coffeeOpt || !grindOpt) return;
        const newImageSrc = subTwinImages[coffeeSel.value] || coffeeOpt.dataset.image;
        if (subImageEl && newImageSrc) {
          subImageEl.removeAttribute('srcset');
          subImageEl.src = newImageSrc;
        }
        const unitCents = parseInt(grindOpt.dataset.price || '0', 10);
        const fullMoneyStr = formatMoney(unitCents);
        // Post-intro price reflects Bundler's 5% subscriber discount.
        const postIntroCents = Math.round(unitCents * (1 - POST_INTRO_DISCOUNT));
        const postIntroMoneyStr = formatMoney(postIntroCents);
        // Both spans show the TOTAL retail value of the whole gift (unit price x number of
        // free deliveries), not the per-unit price. subValueEl previously received fullMoneyStr,
        // which rendered "12 x 500g included (EUR 19.90 retail value)" — the per-bag price where
        // the total belonged. subWasEl is the struck-through price beside the FREE badge.
        const giftTotalMoneyStr = formatMoney(unitCents * subN);
        if (subWasEl) subWasEl.textContent = giftTotalMoneyStr;
        if (subValueEl) subValueEl.textContent = giftTotalMoneyStr;
        const cadence = formatCadence();
        const cadencePhrase = cadence || (isDe ? 'pro Lieferung' : 'per delivery');
        if (subFinePrintEl) {
          subFinePrintEl.textContent = isDe
            ? `Danach ${postIntroMoneyStr} zum Abo-Preis ${cadencePhrase}.`
            : `Then ${postIntroMoneyStr} subscriber price ${cadencePhrase}.`;
        }
        if (subTagPriceEl) subTagPriceEl.textContent = postIntroMoneyStr;
        if (subTagCadenceEl) subTagCadenceEl.textContent = cadencePhrase;
        subRow.dataset.defaultVariant = String(grindOpt.value);
        subRow.dataset.defaultPrice = String(unitCents);
        if (freqSel?.value) subRow.dataset.sellingPlan = String(freqSel.value);
      }

      coffeeSel?.addEventListener('change', () => { rebuildGrindOptions(); updateSubRowDisplay(); });
      grindSel?.addEventListener('change', updateSubRowDisplay);
      freqSel?.addEventListener('change', () => {
        if (freqSel.value) subRow.dataset.sellingPlan = String(freqSel.value);
        updateSubRowDisplay();
      });
      rebuildGrindOptions();
      updateSubRowDisplay();
    }

    // ── Coffee-offer wiring ─────────────────────────────────────────
    // "Add any coffee, save X off the machine". The size <select> carries the variant ids and is
    // tagged data-feq-select, so getTicked() already handles adding it to the cart and pricing it.
    // The only thing needed here is refilling that select when the coffee changes.
    const coffeeOfferProductSel = block.querySelector("[data-feq-coffee-offer-product]");
    const coffeeOfferSizeSel = block.querySelector("[data-feq-coffee-offer-size]");
    if (coffeeOfferProductSel && coffeeOfferSizeSel) {
      // Computed rather than written into the template: a 250g bag costs less than the discount so
      // the total falls, a 1kg bag costs more than it so the total rises. Asserting either one
      // unconditionally would be a false claim on the other. Runs on BOTH coffee and size change.
      refreshCoffeeOfferMachine = () => {
        const wasEl = block.querySelector("[data-feq-machine-was]");
        const nowEl = block.querySelector("[data-feq-machine-now]");
        if (!wasEl || !nowEl || !(coffeeOffCents > 0)) return;
        const entry = machineVariantMap[getMachineVariantId()];
        if (!entry) return;
        wasEl.textContent = formatMoney(entry.price);
        nowEl.textContent = formatMoney(Math.max(0, entry.price - coffeeOffCents));
      };
      const updateCoffeeOfferValue = () => {
        const valueEl = block.querySelector("[data-feq-coffee-offer-value]");
        if (!valueEl || !(coffeeOffCents > 0)) return;
        const opt = coffeeOfferSizeSel.options[coffeeOfferSizeSel.selectedIndex];
        const net = parseInt(opt?.dataset.price || "0", 10) - coffeeOffCents;
        const off = formatMoneyShort(coffeeOffCents);
        if (net < 0) {
          valueEl.innerHTML = isDe
            ? `Mit ${off} Rabatt sinkt dein Gesamtpreis um <strong>${formatMoney(-net)}</strong>.`
            : `With ${off} off, your total drops by <strong>${formatMoney(-net)}</strong>.`;
        } else if (net > 0) {
          valueEl.innerHTML = isDe
            ? `Mit ${off} Rabatt kostet dich dieser Kaffee effektiv <strong>${formatMoney(net)}</strong>.`
            : `With ${off} off, this bag effectively costs <strong>${formatMoney(net)}</strong>.`;
        } else {
          valueEl.innerHTML = isDe
            ? `Mit ${off} Rabatt ist dieser Kaffee effektiv <strong>gratis</strong>.`
            : `With ${off} off, this bag is effectively <strong>free</strong>.`;
        }
      };
      const rebuildCoffeeSizes = () => {
        const list = coffeeOfferVariants[coffeeOfferProductSel.value] || [];
        const prev = coffeeOfferSizeSel.options[coffeeOfferSizeSel.selectedIndex]?.textContent;
        coffeeOfferSizeSel.innerHTML = "";
        let pick = 0, fallback = 0, matched = false;
        list.forEach((v, i) => {
          const opt = document.createElement("option");
          opt.value = String(v.id);
          opt.dataset.price = String(v.price);
          opt.textContent = v.size;
          coffeeOfferSizeSel.appendChild(opt);
          if (prev && v.size === prev) { pick = i; matched = true; }
          // Prefer 250g: it is the default entry bag for this offer, and it keeps a switch from a
          // taster set ("3 x 250g") landing somewhere deliberate rather than on whatever is first.
          else if (!matched && /^250s?g$/i.test(v.size)) fallback = i;
        });
        if (!matched) pick = fallback;
        if (list.length) coffeeOfferSizeSel.selectedIndex = pick;
        const row = coffeeOfferSizeSel.closest("[data-feq-item]");
        if (row) updateRowPrice(row);
        updateCoffeeOfferValue();
        const img = block.querySelector("[data-feq-coffee-offer-img]");
        const chosen = coffeeOfferProductSel.options[coffeeOfferProductSel.selectedIndex];
        if (img && chosen) {
          img.src = chosen.dataset.img || "";
          img.alt = chosen.textContent;
        }
      };
      coffeeOfferProductSel.addEventListener("change", rebuildCoffeeSizes);
      coffeeOfferSizeSel.addEventListener("change", updateCoffeeOfferValue);
      refreshCoffeeOfferMachine();
      rebuildCoffeeSizes();
    }

    function syncColours() {
      const colour = getMachineColour();
      if (!colour) return;
      block.querySelectorAll('[data-feq-select]').forEach(sel => {
        if (manuallyChanged.has(sel)) return;
        const match = [...sel.options].find(o => (o.dataset.colour || '').trim() === colour);
        if (match && !match.disabled) sel.value = match.value;
      });
    }

    function getTicked() {
      const items = [];
      block.querySelectorAll('[data-feq-item]').forEach(item => {
        const check = item.querySelector('[data-feq-check]');
        if (!check?.checked) return;
        const isSubGift = item.hasAttribute('data-feq-sub-gift');
        const select = item.querySelector('[data-feq-select]');
        const discountPct = parseInt(item.dataset.discountPercent || '0', 10);
        let variantId, fullPrice;
        if (isSubGift) {
          variantId = item.dataset.defaultVariant;
          fullPrice = parseInt(item.dataset.defaultPrice || '0', 10);
        } else if (select) {
          variantId = select.value;
          const opt = select.options[select.selectedIndex];
          fullPrice = parseInt(opt?.dataset.price || '0', 10);
        } else {
          variantId = item.dataset.defaultVariant;
          fullPrice = parseInt(item.dataset.defaultPrice || '0', 10);
        }
        const effectivePrice = discountPct > 0
          ? Math.round(fullPrice * (100 - discountPct) / 100)
          : fullPrice;
        const quantity = Math.max(1, parseInt(item.dataset.dealQuantity || '1', 10));
        const entry = {
          variantId: parseInt(variantId, 10),
          price: effectivePrice,
          isDeal: discountPct > 0,
          quantity,
        };
        if (isSubGift) {
          entry.sellingPlan = parseInt(item.dataset.sellingPlan, 10);
          entry.properties = { _intro_gift: 'field-sage-free-subscription' };
        }
        items.push(entry);
      });
      return items;
    }

    // Mirrors what the cart will actually charge: machine + every ticked add-on at its effective
    // price, minus the machine discount when a qualifying coffee is among them. Verified against
    // real cart tests (1498 + 19.90 - 35 = 1482.90; 1498 + 51.90 - 35 = 1514.90).
    const totalEl = block.querySelector('[data-feq-total]');
    function updateTotal() {
      if (!totalEl) return;
      const machine = machineVariantMap[getMachineVariantId()];
      const items = getTicked();
      if (!machine || !items.length) { totalEl.hidden = true; return; }
      let sum = machine.price;
      let qualifies = false;
      for (const it of items) {
        sum += it.price * (it.quantity || 1);
        if (coffeeOfferVariantIds.has(String(it.variantId))) qualifies = true;
      }
      const off = qualifies ? coffeeOffCents : 0;
      sum -= off;
      totalEl.querySelector('[data-feq-total-value]').textContent = formatMoney(Math.max(0, sum));
      const note = totalEl.querySelector('[data-feq-total-note]');
      note.textContent = off > 0
        ? (isDe ? `inkl. ${formatMoneyShort(off)} Rabatt auf die Maschine` : `includes ${formatMoneyShort(off)} off this machine`)
        : '';
      totalEl.hidden = false;
    }

    function refreshAll() {
      syncColours();
      refreshCoffeeOfferMachine();
      block.querySelectorAll('[data-feq-item]').forEach(updateRowPrice);
      updateTotal();
    }

    const idInput = form.querySelector('input[name="id"]');
    if (idInput) {
      new MutationObserver(refreshAll).observe(idInput, { attributes: true, attributeFilter: ['value'] });
    }
    form.addEventListener('change', () => setTimeout(refreshAll, 30));
    block.addEventListener('change', () => setTimeout(updateTotal, 0));

    syncColours();
    block.querySelectorAll('[data-feq-item]').forEach(updateRowPrice);

    // ── Info modal (ⓘ button → opens lightbox with product details) ──
    const modal = block.querySelector('[data-feq-modal]');
    if (modal) {
      const modalTitle = modal.querySelector('[data-feq-modal-title]');
      const modalBody = modal.querySelector('[data-feq-modal-body]');
      const modalImage = modal.querySelector('[data-feq-modal-image]');
      const modalImageWrap = modal.querySelector('[data-feq-modal-image-wrap]');
      let previouslyFocused = null;
      const bodyCache = new Map();

      async function openInfoModal(triggerBtn) {
        const title = triggerBtn.dataset.feqInfoTitle || '';
        const image = triggerBtn.dataset.feqInfoImage || '';
        const handle = triggerBtn.dataset.feqInfoHandle || '';
        const staticId = triggerBtn.dataset.feqInfoStaticId || '';
        modalTitle.textContent = title;
        if (image) {
          modalImage.src = image;
          modalImage.alt = title;
          modalImageWrap.style.display = '';
        } else {
          modalImageWrap.style.display = 'none';
        }
        modalBody.innerHTML = '<p style="opacity:0.55;font-style:italic;">' + (isDe ? 'Lädt…' : 'Loading…') + '</p>';
        previouslyFocused = document.activeElement;
        modal.hidden = false;
        modal.setAttribute('aria-hidden', 'false');
        document.documentElement.style.overflow = 'hidden';
        setTimeout(() => modal.querySelector('[data-feq-modal-close]')?.focus(), 50);

        // Static content path — used by the sub-gift tile (subscription mechanics + blend descriptions live inline in Liquid).
        if (staticId) {
          const staticEl = document.getElementById(staticId);
          if (staticEl) {
            modalBody.innerHTML = staticEl.innerHTML;
          } else {
            modalBody.innerHTML = '<p style="opacity:0.7;font-style:italic;">' + (isDe ? 'Inhalt nicht gefunden.' : 'Content not found.') + '</p>';
          }
          return;
        }

        if (!handle) { modalBody.innerHTML = ''; return; }
        try {
          let body = bodyCache.get(handle);
          if (body === undefined) {
            const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
            const resp = await fetch(root + 'products/' + encodeURIComponent(handle) + '.json', { credentials: 'same-origin' });
            if (!resp.ok) throw new Error('HTTP ' + resp.status);
            const data = await resp.json();
            body = (data && data.product && data.product.body_html) || '';
            bodyCache.set(handle, body);
          }
          if (!modal.hidden && modalTitle.textContent === title) {
            modalBody.innerHTML = body || '<p style="opacity:0.7;font-style:italic;">' + (isDe ? 'Keine weiteren Informationen.' : 'No additional information.') + '</p>';
          }
        } catch (e) {
          console.error('[feq-bundle] body fetch failed for', handle, e);
          if (!modal.hidden && modalTitle.textContent === title) {
            modalBody.innerHTML = '<p style="opacity:0.7;font-style:italic;">' + (isDe ? 'Details konnten nicht geladen werden.' : 'Could not load details.') + '</p>';
          }
        }
      }
      function closeInfoModal() {
        modal.hidden = true;
        modal.setAttribute('aria-hidden', 'true');
        document.documentElement.style.overflow = '';
        previouslyFocused?.focus?.();
      }
      block.querySelectorAll('[data-feq-info-trigger]').forEach(btn => {
        btn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); openInfoModal(btn); });
      });
      modal.addEventListener('click', (e) => {
        if (e.target.closest('[data-feq-modal-close]')) { closeInfoModal(); return; }
        if (!e.target.closest('.feq-bundle__modal-content')) { closeInfoModal(); }
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !modal.hidden) closeInfoModal();
      });
    }

    // ── The hijack ────────────────────────────────────────────────
    // Intercept the submit button's CLICK to do a multi-item POST instead.
    submitBtn.addEventListener('click', async (evt) => {
      const ticked = getTicked();
      console.log('[feq-bundle] submit click, ticked:', ticked.length);
      if (ticked.length === 0) return;

      evt.preventDefault();
      evt.stopImmediatePropagation();

      const machineId = form.querySelector('input[name="id"]')?.value;
      if (!machineId) return;
      const quantity = parseInt(form.querySelector('input[name="quantity"]')?.value || '1', 10);

      const items = [{ id: parseInt(machineId, 10), quantity }];
      ticked.forEach(acc => {
        const lineItem = { id: acc.variantId, quantity: acc.quantity || 1 };
        if (acc.sellingPlan) lineItem.selling_plan = acc.sellingPlan;
        if (acc.properties) lineItem.properties = acc.properties;
        items.push(lineItem);
      });

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const submitButton = form.querySelector('[type="submit"]');
      const spinner = form.querySelector('.loading-overlay__spinner');
      submitButton?.setAttribute('aria-disabled', 'true');
      submitButton?.classList.add('loading');
      spinner?.classList.remove('hidden');

      const sections = (cart?.getSectionsToRender?.() || []).map(s => s.id);
      const payload = { items };
      if (sections.length) {
        payload.sections = sections.join(',');
        payload.sections_url = window.location.pathname;
      }
      cart?.setActiveElement?.(document.activeElement);

      try {
        const resp = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(payload),
        });
        const data = await resp.json();
        if (data.status) {
          const errEl = form.querySelector('.product-form__error-message-wrapper');
          const errMsg = form.querySelector('.product-form__error-message');
          if (errEl && errMsg) {
            errMsg.textContent = data.description || data.message || 'Could not add to cart';
            errEl.removeAttribute('hidden');
          } else {
            console.error('[feq-bundle] cart add error:', data);
          }
          return;
        }
        if (cart) {
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          cart.renderContents(data);
        } else {
          window.location.href = window.routes?.cart_url || '/cart';
        }
      } catch (e) {
        console.error('[feq-bundle] add failed:', e);
      } finally {
        submitButton?.classList.remove('loading');
        submitButton?.removeAttribute('aria-disabled');
        spinner?.classList.add('hidden');
      }
    });
  }
})();
